import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useAuthContext } from "@context-providers/auth/auth-context";
import { SecuredRoute, SecuredRouteProps } from "@router/secured-route";
import { HttpUtils } from "@stellar/utils";
import { useEffect, useState } from "react";

type AuthenticatedRouteProps = SecuredRouteProps;

/**
 * Component to add to routes that require authentication, if the user is authenticated displays
 * the content and if not requests a login.
 * It can optionally add a required company role prop to make it also a secured route based
 * on the user company role.
 * The required company role defines the minimum company role that the user can have,
 * and if it doesn't match that criteria it redirects to a forbidden page.
 */
export function AuthenticatedRoute({
  children,
  requiredRoleCompanyLevel,
  requiredFeatureCompanyLevel,
  requiredPermissionProjectLevel,
}: AuthenticatedRouteProps): JSX.Element | null {
  const { showSessionExpiredDialog } = useAuthContext();

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(true);

  /**
   * Checks if the user is authenticated before rendering the children component.
   * Issues a request to the isLoggedIn backend endpoint. If it succeeds it allows to continue.
   * If the request fails the CoreAPI backend returns an error code 403 "FORBIDDEN" when the
   * user it not authenticated. In this case shows the session expired dialog to the user and don't allow to continue
   */
  useEffect(() => {
    async function checkAuth(): Promise<void> {
      try {
        await faroCoreApiClient.users.isLoggedIn();
        setIsAuthenticated(true);
      } catch (error) {
        // Core API returns an error with status code 401 if the user is not authenticated
        const errorStatus = HttpUtils.getErrorStatus(error);
        if (
          errorStatus &&
          errorStatus === HttpUtils.HTTP_ERROR_CODE.Unauthorized
        ) {
          showSessionExpiredDialog();
          setIsAuthenticated(false);
        }
      }
    }

    void checkAuth();
  }, [showSessionExpiredDialog]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  const returnJsx = isAuthenticated ? <>{children}</> : null;

  if (
    requiredRoleCompanyLevel ||
    requiredFeatureCompanyLevel ||
    requiredPermissionProjectLevel
  ) {
    return (
      <SecuredRoute
        requiredRoleCompanyLevel={requiredRoleCompanyLevel}
        requiredFeatureCompanyLevel={requiredFeatureCompanyLevel}
        requiredPermissionProjectLevel={requiredPermissionProjectLevel}
      >
        {returnJsx}
      </SecuredRoute>
    );
  }

  return returnJsx;
}
